/**
 * Follow from anywhere (spec 11 card 3, §3.4 row 3). Selecting a name, record number or phrase inside
 * any summary (elements marked `data-follow-source`) shows "+ Follow {type} '{text}'" under the
 * selection; one tap follows it (toast with Undo from followsStore; the chip pops into the Following
 * bar). The transcript has its own selection toolbar with the same Follow action.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { followsStore } from "../../stores/followsStore";
import { meetingStore } from "../../stores/meetingStore";
import { followingStore } from "../../stores/followingStore";
import { followFromSelection, isFollowed, type FollowDraft } from "../../lib/followText";

/** Everyone the page knows by name (the meeting's speakers and the Following view's people). */
export const knownPeople = () => ({ ...followingStore.getState().people, ...meetingStore.getState().people });

/** Follow what was selected; the toast and Undo come from followsStore. */
export function followDraft(d: FollowDraft) {
  return followsStore.getState().follow({ type: d.type, label: d.label, ...(d.refId ? { refId: d.refId } : {}), ...(d.keyword ? { keyword: d.keyword } : {}) });
}

export function FollowSelection() {
  const [pick, setPick] = useState<{ draft: FollowDraft; x: number; y: number } | null>(null);
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const check = () => {
      const sel = getSelection();
      if (!sel || sel.isCollapsed || !sel.rangeCount) { setPick(null); return; }
      const range = sel.getRangeAt(0);
      const node = range.commonAncestorContainer;
      const host = (node.nodeType === 1 ? (node as Element) : node.parentElement)?.closest("[data-follow-source]");
      if (!host) { setPick(null); return; }
      const draft = followFromSelection(sel.toString(), knownPeople());
      if (!draft || isFollowed(followsStore.getState().follows, draft)) { setPick(null); return; }
      const r = range.getBoundingClientRect();
      setPick({ draft, x: r.left, y: r.bottom + 10 });
    };
    const onUp = () => requestAnimationFrame(check);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setPick(null); else if (e.shiftKey || e.key === "Shift") requestAnimationFrame(check); };
    const onChange = () => { const s = getSelection(); if (!s || s.isCollapsed) setPick(null); };
    const onScroll = () => setPick(null);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("keyup", onKey);
    document.addEventListener("selectionchange", onChange);
    addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("keyup", onKey);
      document.removeEventListener("selectionchange", onChange);
      removeEventListener("scroll", onScroll);
    };
  }, []);

  // Keep the pill inside the window.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !pick) return;
    el.style.left = `${Math.max(8, Math.min(pick.x, innerWidth - el.offsetWidth - 8))}px`;
  }, [pick]);

  if (!pick) return null;
  const { draft } = pick;
  return (
    <button
      ref={ref}
      className="follow-float"
      style={{ left: pick.x, top: pick.y }}
      onMouseDown={(e) => e.preventDefault()} // keep the selection until the click lands
      onClick={() => { setPick(null); getSelection()?.removeAllRanges(); void followDraft(draft); }}
    >
      <Plus size={12} />Follow {draft.type} “{draft.quoted}”
    </button>
  );
}
